import { type Static, Type } from "@sinclair/typebox";
import { Value } from "@sinclair/typebox/value";
import { isOrderNumber, isOrderNumberForOrderId, OrderNumberSchema } from "./order-number.js";
import {
  remainingTrackedSaleInventoryValueFen,
  TrackedSaleInventoryCostSnapshotSchema,
  trackedSaleInventoryCostSnapshotMatchesItem,
} from "./sale-inventory.js";

export const PRODUCT_COST_BUSINESS_METRIC_DETAIL_CONTRACT_VERSION =
  "PRODUCT_COST_BUSINESS_METRIC_DETAIL_V1" as const;
export const PRODUCT_COST_BUSINESS_METRIC_DETAIL_CAPACITY = 5_000;

export const ProductCostRecordNumberSchema = Type.String({
  pattern: "^CB-[0-9]{8}-[0-9A-F]{12}$",
});
export type ProductCostRecordNumber = `CB-${string}-${string}`;

export const ProductCostBasisSchema = Type.Union([
  Type.Literal("TRACKED_MOVING_AVERAGE"),
  Type.Literal("PRODUCT_COST_SNAPSHOT"),
  Type.Literal("UNKNOWN"),
]);
export type ProductCostBasis = Static<typeof ProductCostBasisSchema>;

const PRODUCT_COST_BASIS_TERMS = {
  TRACKED_MOVING_AVERAGE: {
    label: "销售时移动平均成本",
    explanation: "按商品出库时已经冻结的移动平均库存价值计算，后续采购价格变化不会改写本行。",
  },
  PRODUCT_COST_SNAPSHOT: {
    label: "销售时商品成本",
    explanation: "商品未跟踪库存时，使用销售当时已经冻结的商品成本。",
  },
  UNKNOWN: {
    label: "成本待补充",
    explanation: "旧记录没有可核对的成交时成本，本行不会被当作 0 元计入合计。",
  },
} as const satisfies Record<ProductCostBasis, { label: string; explanation: string }>;

export function productCostBasisTerm(basis: ProductCostBasis): {
  label: string;
  explanation: string;
} {
  return PRODUCT_COST_BASIS_TERMS[basis];
}

const FNV_1A_64_OFFSET = 0xcbf29ce484222325n;
const FNV_1A_64_PRIME = 0x100000001b3n;
const LOWER_48_BITS = 0xffffffffffffn;
const STABLE_ID_PATTERN = /^[A-Za-z0-9][A-Za-z0-9._:-]{11,127}$/u;

function isCanonicalDate(value: string): boolean {
  const milliseconds = Date.parse(`${value}T00:00:00.000Z`);
  return (
    /^\d{4}-\d{2}-\d{2}$/u.test(value) &&
    Number.isFinite(milliseconds) &&
    new Date(milliseconds).toISOString().slice(0, 10) === value
  );
}

function isCanonicalTimestamp(value: string): boolean {
  const milliseconds = Date.parse(value);
  return Number.isFinite(milliseconds) && new Date(milliseconds).toISOString() === value;
}

function fortyEightBitHex(value: string): string {
  let hash = FNV_1A_64_OFFSET;
  for (const character of value) {
    hash ^= BigInt(character.codePointAt(0) ?? 0);
    hash = BigInt.asUintN(64, hash * FNV_1A_64_PRIME);
  }
  return (hash & LOWER_48_BITS).toString(16).toUpperCase().padStart(12, "0");
}

export function deriveProductCostRecordNumber(
  businessDate: string,
  itemId: string,
): ProductCostRecordNumber {
  if (!isCanonicalDate(businessDate)) {
    throw new TypeError("businessDate must be a real calendar date in YYYY-MM-DD format");
  }
  if (!STABLE_ID_PATTERN.test(itemId)) {
    throw new TypeError("itemId must contain at least 12 safe characters");
  }
  return `CB-${businessDate.replaceAll("-", "")}-${fortyEightBitHex(
    `CB\0${businessDate}\0${itemId}`,
  )}`;
}

export function isProductCostRecordNumber(value: unknown): value is ProductCostRecordNumber {
  if (!Value.Check(ProductCostRecordNumberSchema, value)) return false;
  const match = /^CB-(\d{4})(\d{2})(\d{2})-[0-9A-F]{12}$/u.exec(value);
  return Boolean(match && isCanonicalDate(`${match[1]}-${match[2]}-${match[3]}`));
}

const StableIdSchema = Type.String({
  minLength: 1,
  maxLength: 128,
  pattern: "^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$",
});
const SafeQuantitySchema = Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER });
const PositiveQuantitySchema = Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER });
const SafeMoneyFenSchema = Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER });
const NullableMoneyFenSchema = Type.Union([SafeMoneyFenSchema, Type.Null()]);
const ProductVersionSchema = Type.Union([
  Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
  Type.Null(),
]);

const SharedItemProperties = {
  costRecordNumber: ProductCostRecordNumberSchema,
  orderNumber: OrderNumberSchema,
  orderBusinessDate: Type.String({ pattern: "^\\d{4}-\\d{2}-\\d{2}$" }),
  saleBusinessDate: Type.String({ pattern: "^\\d{4}-\\d{2}-\\d{2}$" }),
  tableNumber: Type.String({ minLength: 1, maxLength: 40 }),
  productCode: Type.String({ minLength: 1, maxLength: 128 }),
  productName: Type.String({ minLength: 1, maxLength: 200 }),
  soldQuantity: PositiveQuantitySchema,
  returnedQuantity: SafeQuantitySchema,
  remainingQuantity: PositiveQuantitySchema,
  costBasis: ProductCostBasisSchema,
  remainingCostFen: NullableMoneyFenSchema,
  occurredAt: Type.String({ minLength: 1, maxLength: 64 }),
} as const;

export const ProductCostBusinessMetricDetailSourceItemSchema = Type.Object(
  {
    itemId: StableIdSchema,
    orderId: StableIdSchema,
    skuId: StableIdSchema,
    productVersion: ProductVersionSchema,
    unitCostFen: SafeMoneyFenSchema,
    costSnapshotKnown: Type.Boolean(),
    inventoryCostSnapshot: Type.Union([TrackedSaleInventoryCostSnapshotSchema, Type.Null()]),
    ...SharedItemProperties,
  },
  { additionalProperties: false },
);
export type ProductCostBusinessMetricDetailSourceItem = Static<
  typeof ProductCostBusinessMetricDetailSourceItemSchema
>;

export const ProductCostBusinessMetricDetailItemSchema = Type.Object(SharedItemProperties, {
  additionalProperties: false,
});
export type ProductCostBusinessMetricDetailItem = Static<
  typeof ProductCostBusinessMetricDetailItemSchema
>;

const EnvelopeProperties = {
  contractVersion: Type.Literal(PRODUCT_COST_BUSINESS_METRIC_DETAIL_CONTRACT_VERSION),
  tenantId: Type.String({ minLength: 1, maxLength: 128 }),
  storeId: Type.String({ minLength: 1, maxLength: 128 }),
  businessDate: Type.String({ pattern: "^\\d{4}-\\d{2}-\\d{2}$" }),
  timeZone: Type.String({ minLength: 1, maxLength: 80 }),
  businessDayCutover: Type.String({ pattern: "^(?:[01]\\d|2[0-3]):[0-5]\\d$" }),
  rangeStartedAt: Type.String({ minLength: 1, maxLength: 64 }),
  rangeEndedAt: Type.String({ minLength: 1, maxLength: 64 }),
  generatedAt: Type.String({ minLength: 1, maxLength: 64 }),
  coverage: Type.Literal("CURRENT_BUSINESS_DAY_PRODUCT_COST_LEDGER"),
  valueFen: SafeMoneyFenSchema,
  factCount: Type.Integer({
    minimum: 0,
    maximum: PRODUCT_COST_BUSINESS_METRIC_DETAIL_CAPACITY,
  }),
  itemCount: Type.Integer({
    minimum: 0,
    maximum: PRODUCT_COST_BUSINESS_METRIC_DETAIL_CAPACITY,
  }),
  unknownFactCount: Type.Integer({
    minimum: 0,
    maximum: PRODUCT_COST_BUSINESS_METRIC_DETAIL_CAPACITY,
  }),
} as const;

export const ProductCostBusinessMetricDetailSnapshotSchema = Type.Object(
  {
    ...EnvelopeProperties,
    items: Type.Array(ProductCostBusinessMetricDetailSourceItemSchema, {
      maxItems: PRODUCT_COST_BUSINESS_METRIC_DETAIL_CAPACITY,
    }),
  },
  { additionalProperties: false },
);
export type ProductCostBusinessMetricDetailSnapshot = Static<
  typeof ProductCostBusinessMetricDetailSnapshotSchema
>;

export const ProductCostBusinessMetricDetailReadModelSchema = Type.Object(
  {
    ...EnvelopeProperties,
    items: Type.Array(ProductCostBusinessMetricDetailItemSchema, {
      maxItems: PRODUCT_COST_BUSINESS_METRIC_DETAIL_CAPACITY,
    }),
  },
  { additionalProperties: false },
);
export type ProductCostBusinessMetricDetailReadModel = Static<
  typeof ProductCostBusinessMetricDetailReadModelSchema
>;

type DetailEnvelope = Pick<
  ProductCostBusinessMetricDetailReadModel,
  | "tenantId"
  | "storeId"
  | "businessDate"
  | "timeZone"
  | "rangeStartedAt"
  | "rangeEndedAt"
  | "generatedAt"
  | "valueFen"
  | "factCount"
  | "itemCount"
  | "unknownFactCount"
>;

type SharedItem = ProductCostBusinessMetricDetailItem;

function envelopeIsConsistent(value: DetailEnvelope): boolean {
  if (
    !isCanonicalDate(value.businessDate) ||
    !isCanonicalTimestamp(value.rangeStartedAt) ||
    !isCanonicalTimestamp(value.rangeEndedAt) ||
    !isCanonicalTimestamp(value.generatedAt) ||
    Date.parse(value.rangeEndedAt) <= Date.parse(value.rangeStartedAt) ||
    Date.parse(value.generatedAt) < Date.parse(value.rangeStartedAt) ||
    Date.parse(value.generatedAt) >= Date.parse(value.rangeEndedAt) ||
    value.factCount > value.itemCount ||
    value.unknownFactCount !== value.itemCount - value.factCount
  ) {
    return false;
  }
  try {
    new Intl.DateTimeFormat("zh-CN", { timeZone: value.timeZone }).format(new Date(0));
  } catch {
    return false;
  }
  return true;
}

function sharedItemIsConsistent(item: SharedItem, envelope: DetailEnvelope): boolean {
  const occurredAt = Date.parse(item.occurredAt);
  return (
    isProductCostRecordNumber(item.costRecordNumber) &&
    item.costRecordNumber.slice(3, 11) === envelope.businessDate.replaceAll("-", "") &&
    isCanonicalDate(item.orderBusinessDate) &&
    isOrderNumber(item.orderNumber) &&
    item.orderNumber.slice(3, 11) === item.orderBusinessDate.replaceAll("-", "") &&
    item.saleBusinessDate === envelope.businessDate &&
    isCanonicalTimestamp(item.occurredAt) &&
    occurredAt >= Date.parse(envelope.rangeStartedAt) &&
    occurredAt < Date.parse(envelope.rangeEndedAt) &&
    occurredAt <= Date.parse(envelope.generatedAt) &&
    item.returnedQuantity <= item.soldQuantity &&
    item.remainingQuantity === item.soldQuantity - item.returnedQuantity &&
    item.remainingQuantity > 0 &&
    (item.costBasis === "UNKNOWN") === (item.remainingCostFen === null) &&
    item.tableNumber === item.tableNumber.trim() &&
    item.productCode === item.productCode.trim() &&
    item.productName === item.productName.trim()
  );
}

function itemsAreConsistent(items: readonly SharedItem[], envelope: DetailEnvelope): boolean {
  if (items.length !== envelope.itemCount) return false;
  const recordNumbers = new Set<string>();
  let knownValueFen = 0;
  let knownFactCount = 0;
  let previousOccurredAt = Number.POSITIVE_INFINITY;
  let previousRecordNumber = "";
  for (const item of items) {
    const occurredAt = Date.parse(item.occurredAt);
    if (
      recordNumbers.has(item.costRecordNumber) ||
      !sharedItemIsConsistent(item, envelope) ||
      occurredAt > previousOccurredAt ||
      (occurredAt === previousOccurredAt &&
        previousRecordNumber !== "" &&
        item.costRecordNumber <= previousRecordNumber)
    ) {
      return false;
    }
    if (item.remainingCostFen !== null) {
      knownValueFen += item.remainingCostFen;
      knownFactCount += 1;
      if (!Number.isSafeInteger(knownValueFen)) return false;
    }
    recordNumbers.add(item.costRecordNumber);
    previousOccurredAt = occurredAt;
    previousRecordNumber = item.costRecordNumber;
  }
  return knownValueFen === envelope.valueFen && knownFactCount === envelope.factCount;
}

export function productCostBasisForSource(
  source: Pick<
    ProductCostBusinessMetricDetailSourceItem,
    "costSnapshotKnown" | "inventoryCostSnapshot"
  >,
): ProductCostBasis {
  if (source.inventoryCostSnapshot !== null) return "TRACKED_MOVING_AVERAGE";
  return source.costSnapshotKnown ? "PRODUCT_COST_SNAPSHOT" : "UNKNOWN";
}

export function remainingProductCostFenForSource(
  source: Pick<
    ProductCostBusinessMetricDetailSourceItem,
    | "soldQuantity"
    | "returnedQuantity"
    | "unitCostFen"
    | "costSnapshotKnown"
    | "inventoryCostSnapshot"
  >,
): number | null {
  if (source.inventoryCostSnapshot !== null) {
    try {
      return remainingTrackedSaleInventoryValueFen(
        source.inventoryCostSnapshot,
        source.returnedQuantity,
      );
    } catch {
      return null;
    }
  }
  if (!source.costSnapshotKnown) return null;
  const value = (source.soldQuantity - source.returnedQuantity) * source.unitCostFen;
  return Number.isSafeInteger(value) ? value : null;
}

function sourceItemIsConsistent(
  item: ProductCostBusinessMetricDetailSourceItem,
  envelope: DetailEnvelope,
): boolean {
  if (
    !sharedItemIsConsistent(item, envelope) ||
    item.productCode !== item.skuId ||
    item.costBasis !== productCostBasisForSource(item) ||
    item.remainingCostFen !== remainingProductCostFenForSource(item) ||
    !isOrderNumberForOrderId(item.orderNumber, item.orderId, item.orderBusinessDate)
  ) {
    return false;
  }
  if (item.inventoryCostSnapshot !== null) {
    return (
      item.productVersion !== null &&
      trackedSaleInventoryCostSnapshotMatchesItem(item.inventoryCostSnapshot, {
        skuId: item.skuId,
        productVersion: item.productVersion,
        unitCostFen: item.unitCostFen,
        costSnapshotKnown: item.costSnapshotKnown,
        quantity: item.soldQuantity,
      })
    );
  }
  return item.costSnapshotKnown || item.unitCostFen === 0;
}

export function isProductCostBusinessMetricDetailSnapshot(
  value: unknown,
  expectedScope?: { tenantId: string; storeId: string; businessDate: string },
): value is ProductCostBusinessMetricDetailSnapshot {
  if (
    !Value.Check(ProductCostBusinessMetricDetailSnapshotSchema, value) ||
    !envelopeIsConsistent(value) ||
    (expectedScope !== undefined &&
      (value.tenantId !== expectedScope.tenantId ||
        value.storeId !== expectedScope.storeId ||
        value.businessDate !== expectedScope.businessDate))
  ) {
    return false;
  }
  const itemIds = new Set<string>();
  for (const item of value.items) {
    let expectedRecordNumber: ProductCostRecordNumber;
    try {
      expectedRecordNumber = deriveProductCostRecordNumber(value.businessDate, item.itemId);
    } catch {
      return false;
    }
    if (
      itemIds.has(item.itemId) ||
      item.costRecordNumber !== expectedRecordNumber ||
      !sourceItemIsConsistent(item, value)
    ) {
      return false;
    }
    itemIds.add(item.itemId);
  }
  return itemsAreConsistent(value.items, value);
}

export function isProductCostBusinessMetricDetailReadModel(
  value: unknown,
  expectedScope?: { tenantId: string; storeId: string; businessDate: string },
): value is ProductCostBusinessMetricDetailReadModel {
  return (
    Value.Check(ProductCostBusinessMetricDetailReadModelSchema, value) &&
    envelopeIsConsistent(value) &&
    (expectedScope === undefined ||
      (value.tenantId === expectedScope.tenantId &&
        value.storeId === expectedScope.storeId &&
        value.businessDate === expectedScope.businessDate)) &&
    itemsAreConsistent(value.items, value)
  );
}
